// A procedural head of hair for the hair test scene: strands rooted on a scalp
// cap of a sphere, falling under gravity with a little curl and a per-strand
// wobble. Deterministic for a given seed, so a screenshot is reproducible.
// Pure: no three, no DOM. Output is the .hair-shaped record hairFormat.js
// consumes, so the two sources go through one path to the renderer.

import { strandSeed } from './hairFormat.js'

/**
 * @param {object} o
 * @param {number} o.strands      strand count
 * @param {number} o.segments     segments per strand
 * @param {number} o.radius       head radius (metres)
 * @param {number} o.length       strand length (metres)
 * @param {number} o.curl         0 = straight, 1 = tight curl
 * @param {number} o.capAngle     scalp cap half-angle from the top, radians
 * @param {number} o.seed
 * @returns the same shape parseHairFile returns
 */
export function proceduralHair({
  strands = 30000, segments = 16, radius = 0.09, length = 0.28, curl = 0.15, capAngle = 1.9, seed = 1,
} = {}) {
  const points = new Float32Array(strands * (segments + 1) * 3)
  const rand = mulberry32(seed)
  let w = 0
  for (let s = 0; s < strands; s++) {
    // Root: uniform on the spherical cap, tilted back so the face stays bare.
    const u = rand(), v = rand()
    const theta = Math.acos(1 - u * (1 - Math.cos(capAngle)))
    const phi = v * Math.PI * 2
    const rx = Math.sin(theta) * Math.cos(phi), ry = Math.cos(theta), rz = Math.sin(theta) * Math.sin(phi)
    // A root in the front lower cap is the face: mirror it to the back.
    const front = rz > 0.35 && ry < 0.55
    const nz = front ? -rz : rz
    let x = rx * radius, y = ry * radius, z = nz * radius
    // Direction: start along the surface tangent that points down, then bend to gravity.
    const wobble = strandSeed(s) * Math.PI * 2
    const perStrandLen = length * (0.75 + 0.5 * strandSeed(s + 7919))
    let dx = rx, dy = -Math.abs(ry) * 0.2 - 0.3, dz = nz
    const step = perStrandLen / segments
    for (let i = 0; i <= segments; i++) {
      points[w++] = x; points[w++] = y; points[w++] = z
      const t = i / segments
      // Gravity pulls the direction down, the scalp pushes it out, curl spins it.
      dy -= 0.35 * (1 - t)
      const dist = Math.hypot(x, y, z)
      if (dist < radius * 1.02) { dx += x / dist * 0.4; dz += z / dist * 0.4 }
      const spin = wobble + t * Math.PI * 4 * curl
      dx += Math.cos(spin) * curl * 0.6
      dz += Math.sin(spin) * curl * 0.6
      const n = Math.hypot(dx, dy, dz) || 1
      dx /= n; dy /= n; dz /= n
      x += dx * step; y += dy * step; z += dz * step
    }
  }
  return {
    strandCount: strands,
    pointCount: strands * (segments + 1),
    segments: null,
    points,
    defaultSegments: segments,
    defaultThickness: 0.0001,
    defaultColor: [0.05, 0.03, 0.015],
    info: `procedural seed=${seed}`,
  }
}

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
